/**
 * Pure formatting helpers. No React, no domain logic — shape data for
 * display. All output is PT-BR (mono for numbers per Prism SKILL.md).
 */

const VIEW_FORMATTER = new Intl.NumberFormat('pt-BR', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR')

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m.toString()}:${s.toString().padStart(2, '0')}`
}

/**
 * Humanized wall-clock duration in PT-BR: "45 s", "2 min 13 s", "5 min",
 * "1 h 5 min". Used for job / per-video generation times (card #76) where
 * a raw `mm:ss` reads worse than words at a glance.
 */
export function formatDurationHuman(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  if (total < 60) return `${total.toString()} s`
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  if (hours > 0) {
    return minutes > 0 ? `${hours.toString()} h ${minutes.toString()} min` : `${hours.toString()} h`
  }
  return secs > 0 ? `${minutes.toString()} min ${secs.toString()} s` : `${minutes.toString()} min`
}

/** A count without a suffix: whole below a thousand, compact pt-BR from there ("1,2 mil"). */
export function formatCompactNumber(n: number): string {
  if (n < 1000) return NUMBER_FORMATTER.format(n)
  return VIEW_FORMATTER.format(n)
}

export function formatViewCount(views: number): string {
  return `${formatCompactNumber(views)} views`
}

export function formatNumber(n: number): string {
  return NUMBER_FORMATTER.format(n)
}

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

const USD_TINY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
})

/** USD with 2 decimals normally; switches to 4 decimals under $0.01 so
 *  cheap calls still read as a non-zero figure (e.g. "$0.0034"). */
export function formatUSD(amount: number): string {
  if (amount > 0 && amount < 0.01) return USD_TINY_FORMATTER.format(amount)
  return USD_FORMATTER.format(amount)
}

export function formatRelativeDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const date = new Date(iso)
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date)
  } catch {
    return '—'
  }
}

const _SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })

/** "02/11/2026". ``null`` for an empty or invalid ISO, so the caller can omit the line. */
export function formatShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return _SHORT_DATE_FORMATTER.format(date)
}

const _SCHEDULED_DAY_FORMATTER = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' })
const _SCHEDULED_FULL_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long', timeStyle: 'short' })

function _isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function _formatHourMinute(date: Date): string {
  const minutes = date.getMinutes()
  const hour = date.getHours().toString()
  return minutes === 0 ? `${hour}h` : `${hour}h${minutes.toString().padStart(2, '0')}`
}

/** Compact chip label for scheduled videos: "Agendado · hoje 17h" / "Agendado · 14/05 17h". */
export function formatScheduledLabel(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const dayPart = _isSameDay(date, new Date()) ? 'hoje' : _SCHEDULED_DAY_FORMATTER.format(date)
  return `Agendado · ${dayPart} ${_formatHourMinute(date)}`
}

/** Tooltip text with full PT-BR date + relative countdown. */
export function formatScheduledTooltip(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const full = _SCHEDULED_FULL_FORMATTER.format(date)
  const diffMs = date.getTime() - Date.now()
  if (diffMs <= 0) return `Publica ${full} · aguardando`
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) return `Publica ${full} · em ${minutes.toString()}min`
  const hours = Math.floor(minutes / 60)
  const remMin = minutes % 60
  if (hours < 24) {
    return remMin === 0
      ? `Publica ${full} · em ${hours.toString()}h`
      : `Publica ${full} · em ${hours.toString()}h ${remMin.toString()}min`
  }
  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  return remHours === 0
    ? `Publica ${full} · em ${days.toString()}d`
    : `Publica ${full} · em ${days.toString()}d ${remHours.toString()}h`
}

const _DAY_MS = 86_400_000

/** Tempo desde `iso` numa unidade só, a maior que cabe: "hoje", "3 dias",
 *  "2 meses", "1 ano". Mês é 30 dias e ano 365: é para leitura rápida, e a
 *  data exata fica no tooltip. */
export function formatElapsed(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const days = Math.max(0, Math.floor((now.getTime() - date.getTime()) / _DAY_MS))
  const unit = (n: number, one: string, many: string) => `${n.toString()} ${n === 1 ? one : many}`
  if (days < 1) return 'hoje'
  if (days < 30) return unit(days, 'dia', 'dias')
  if (days < 365) return unit(Math.floor(days / 30), 'mês', 'meses')
  return unit(Math.floor(days / 365), 'ano', 'anos')
}
